import React, { useMemo } from "react";
import { usePlayerModal } from "../PlayerModalContext";
import { lookUpPlayerName } from "../playerDisplayName";
import { PickRoundMeta } from "./PickRoundMeta";
import { PlayerAvatarWithScore } from "./PlayerAvatarWithScore";
import { PlayerPickTrigger } from "./PlayerPickTrigger";
import { outsidersSectionLeaderNames } from "../sectionLeaders";
import {
    outsidersLeaderEdgeDetail,
    outsidersLeadingGroupSize,
    sortRankingsByLastThreeBest,
} from "../lastThreeUtils";
import { SectionLeaderNamePill } from "./SectionLeaderNamePill";
import { MissedCutPickCardShell } from "./MissedCutPickCardShell";
import {
    MISSED_CUT_CARD_MIN_HEIGHT_CLASS,
    MISSED_CUT_META_RESERVE_CLASS,
    PickRowAvatarPlaceholder,
} from "./PickRowAvatarPlaceholder";

function outsiderPickDisplayName(person, pick) {
    if (!pick) return null;
    const summ = person.summary?.find(({ name }) => name === pick);
    if (!summ) return pick;
    return lookUpPlayerName(person, pick);
}

export function LastThreeSection({ rankings, formatter, cutPickNames = [] }) {
    const { openPlayer } = usePlayerModal();
    const ordered = useMemo(
        () => sortRankingsByLastThreeBest(rankings),
        [rankings],
    );
    const leaderSet = useMemo(
        () => new Set(outsidersSectionLeaderNames(rankings)),
        [rankings],
    );
    const leadingGroupSize = useMemo(
        () => outsidersLeadingGroupSize(ordered),
        [ordered],
    );
    const cutPickSet = useMemo(
        () => new Set(cutPickNames),
        [cutPickNames],
    );

    return (
        <section
            aria-label="Outsiders"
            className="rankings rounded-xl border border-masters-moss/30 bg-masters-cream/95 p-4 text-masters-forest shadow-lg shadow-black/20 sm:p-6"
        >
            <h2 className="mb-4 border-b border-masters-parchment pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-masters-goldmuted">
                Outsiders
            </h2>
            {ordered.map((row, index) => {
                const { person, lastThree, highlightedPick, sortAmount } =
                    row;
                const inLeadingGroup = index < leadingGroupSize;
                const belowRow = ordered[leadingGroupSize];
                const leaderEdge =
                    inLeadingGroup && belowRow
                        ? outsidersLeaderEdgeDetail(row, belowRow)
                        : null;
                const tieBreakRequired =
                    leaderEdge !== null && leaderEdge.firstDiffIndex >= 1;
                const edgePickIds = tieBreakRequired ? leaderEdge.picks : [];
                const edgePickIdsForLabel = edgePickIds.filter(
                    (p) => p !== highlightedPick,
                );
                const edgeNames = edgePickIdsForLabel
                    .map((p) => outsiderPickDisplayName(person, p))
                    .filter(Boolean);
                const tieBreakerLabel =
                    tieBreakRequired && edgeNames.length > 0
                        ? edgeNames.join(" · ")
                        : null;

                return (
                    <div key={person.name} className="mb-6 last:mb-0">
                        <div className="border-b border-masters-parchment/80 pb-3 pt-1">
                            <div className="flex flex-row items-center justify-between gap-4">
                                <div className="min-w-0 pl-1">
                                    {leaderSet.has(person.name) ? (
                                        <SectionLeaderNamePill
                                            name={person.name}
                                        />
                                    ) : (
                                        <span className="text-base font-semibold">
                                            {person.name}
                                        </span>
                                    )}
                                </div>
                                <div className="text-base font-semibold text-masters-moss">
                                    {formatter.format(
                                        Math.floor(sortAmount),
                                    )}
                                </div>
                            </div>
                        </div>
                        {tieBreakerLabel ? (
                            <p className="mb-2 mt-2 pl-1 text-xs italic text-masters-goldmuted">
                                * Tie-breaker decided by {tieBreakerLabel}
                            </p>
                        ) : null}
                        <div className="space-y-1 pl-1 pt-2 text-sm">
                            {lastThree.map((pick) => {
                                const isHighlight = pick === highlightedPick;
                                const isTieBreakerPick = edgePickIds.includes(
                                    pick,
                                );
                                const summ = person.summary?.find(
                                    ({ name }) => name === pick,
                                );
                                if (!summ && cutPickSet.has(pick)) {
                                    const rowShellClass =
                                        isTieBreakerPick && !isHighlight
                                            ? "w-full min-w-0 rounded-lg border border-masters-moss/45 bg-masters-forestwash p-1.5 sm:p-2"
                                            : "";
                                    return (
                                        <div
                                            key={`${person.name}-${pick}-l3`}
                                            className={`pb-2 ${rowShellClass}`}
                                        >
                                            <MissedCutPickCardShell
                                                className={
                                                    isHighlight
                                                        ? "ring-2 ring-masters-gold/70 ring-offset-2 ring-offset-masters-cream/95"
                                                        : ""
                                                }
                                            >
                                                <div
                                                    className={`flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 ${MISSED_CUT_CARD_MIN_HEIGHT_CLASS} ${
                                                        isHighlight
                                                            ? "bg-masters-parchment"
                                                            : isTieBreakerPick
                                                              ? "bg-transparent"
                                                              : "bg-masters-parchment/40"
                                                    }`}
                                                >
                                                    <PlayerPickTrigger
                                                        ariaLabel={`Player details: ${pick}`}
                                                        onOpen={() =>
                                                            openPlayer(pick)
                                                        }
                                                    >
                                                        <PickRowAvatarPlaceholder />
                                                    </PlayerPickTrigger>
                                                    <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center pl-1">
                                                        <PlayerPickTrigger
                                                            ariaLabel={`Player details: ${pick}`}
                                                            onOpen={() =>
                                                                openPlayer(
                                                                    pick,
                                                                )
                                                            }
                                                        >
                                                            <div className="font-medium text-masters-forest">
                                                                {pick}
                                                            </div>
                                                        </PlayerPickTrigger>
                                                        <div
                                                            className={`mt-0.5 ${MISSED_CUT_META_RESERVE_CLASS}`}
                                                        >
                                                            <PickRoundMeta
                                                                className="mt-0"
                                                                thru={null}
                                                                roundGrossStrokes={
                                                                    undefined
                                                                }
                                                            />
                                                        </div>
                                                        <div className="text-sm text-masters-moss">
                                                            {formatter.format(0)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </MissedCutPickCardShell>
                                        </div>
                                    );
                                }
                                if (summ) {
                                    const rowShellClass =
                                        isTieBreakerPick && !isHighlight
                                            ? "w-full min-w-0 rounded-lg border border-masters-moss/45 bg-masters-forestwash p-1.5 sm:p-2"
                                            : "";
                                    return (
                                        <div
                                            key={`${person.name}-${pick}-l3`}
                                            className={`pb-2 ${rowShellClass}`}
                                        >
                                            <div
                                                className={`flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 ${
                                                    isHighlight
                                                        ? "bg-masters-parchment ring-2 ring-masters-gold/70 ring-offset-2 ring-offset-masters-cream/95"
                                                        : isTieBreakerPick
                                                          ? "bg-transparent"
                                                          : "bg-masters-parchment/40"
                                                }`}
                                            >
                                                <PlayerPickTrigger
                                                    ariaLabel={`Player details: ${pick}`}
                                                    onOpen={() =>
                                                        openPlayer(pick)
                                                    }
                                                >
                                                    <PlayerAvatarWithScore
                                                        src={summ.avatarURL}
                                                        alt=""
                                                        scoreVsPar={
                                                            summ.scoreVsPar
                                                        }
                                                        imgClassName="h-10 w-10"
                                                    />
                                                </PlayerPickTrigger>
                                                <div className="min-w-0 pl-1">
                                                    <PlayerPickTrigger
                                                        ariaLabel={`Player details: ${pick}`}
                                                        onOpen={() =>
                                                            openPlayer(pick)
                                                        }
                                                    >
                                                        <div className="font-medium text-masters-forest">
                                                            {lookUpPlayerName(
                                                                person,
                                                                pick,
                                                            )}
                                                        </div>
                                                    </PlayerPickTrigger>
                                                    <PickRoundMeta
                                                        className="mt-0.5"
                                                        thru={
                                                            summ.roundThruLabel
                                                        }
                                                        roundGrossStrokes={
                                                            summ.completedRoundGross
                                                        }
                                                    />
                                                    <div className="text-sm text-masters-moss">
                                                        {formatter.format(
                                                            summ.amount,
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                const rowShellClassStrike =
                                    isTieBreakerPick && !isHighlight
                                        ? "w-full min-w-0 rounded-lg border border-masters-moss/45 bg-masters-forestwash p-1.5 sm:p-2"
                                        : "";
                                return (
                                    <div
                                        key={`${person.name}-${pick}-l3`}
                                        className={`pb-2 ${rowShellClassStrike}`}
                                    >
                                        <div
                                            className={`rounded-lg px-2 py-1 text-masters-goldmuted ${
                                                isHighlight
                                                    ? "ring-2 ring-masters-gold/70 ring-offset-2 ring-offset-masters-cream/95"
                                                    : isTieBreakerPick
                                                      ? "bg-transparent"
                                                      : ""
                                            }`}
                                        >
                                            <PlayerPickTrigger
                                                ariaLabel={`Player details: ${pick}`}
                                                onOpen={() => openPlayer(pick)}
                                            >
                                                <div className="line-through decoration-masters-goldmuted/60">
                                                    {pick}
                                                </div>
                                            </PlayerPickTrigger>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </section>
    );
}
